/**
 * Feature Flag Manager
 *
 * Manages feature flag state and provides utilities for checking flag values.
 */

import {
  type FeatureFlag,
  type FeatureFlagValues,
  type FeatureFlagManagerConfig,
} from './types';
import { FEATURE_FLAG_CONFIG, getDefaultFeatureFlagValues } from './config';

/**
 * Singleton manager for feature flags
 */
export class FeatureFlagManager {
  private static instance: FeatureFlagManager | null = null;
  private flags: FeatureFlagValues;
  private config: FeatureFlagManagerConfig;
  private initialized = false;

  private constructor(config: FeatureFlagManagerConfig = {}) {
    this.config = config;
    this.flags = {} as FeatureFlagValues;
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(
    config?: FeatureFlagManagerConfig
  ): FeatureFlagManager {
    if (!FeatureFlagManager.instance) {
      FeatureFlagManager.instance = new FeatureFlagManager(config);
    }
    return FeatureFlagManager.instance;
  }

  /**
   * Initialize the feature flags from environment
   */
  public initialize(): void {
    if (this.initialized) {
      return;
    }

    this.flags = this.loadFromEnvironment();
    this.initialized = true;

    if (this.config.debug) {
      console.log('[FeatureFlags] Initialized with values:', this.flags);
    }
  }

  /**
   * Load feature flag values from environment variables
   */
  private loadFromEnvironment(): FeatureFlagValues {
    const defaultValues = getDefaultFeatureFlagValues();
    const flags = { ...defaultValues };

    // Check each flag's environment variable
    Object.entries(FEATURE_FLAG_CONFIG).forEach(([flagKey, config]) => {
      const envVar = config.envVar;
      if (!envVar) {
        return;
      }

      // Check for override first (for testing)
      const envValue =
        this.config.envOverrides?.[envVar] ?? this.getEnvVar(envVar);

      if (envValue !== undefined) {
        flags[flagKey as FeatureFlag] = this.parseBoolean(envValue);

        if (this.config.debug) {
          console.log(
            `[FeatureFlags] ${flagKey}: ${flags[flagKey as FeatureFlag]} (from ${envVar})`
          );
        }
      } else if (this.config.debug) {
        console.log(
          `[FeatureFlags] ${flagKey}: ${flags[flagKey as FeatureFlag]} (default)`
        );
      }
    });

    return flags;
  }

  /**
   * Get environment variable value (browser-safe)
   */
  private getEnvVar(key: string): string | undefined {
    // In browser, check process.env (from build-time injection)
    if (typeof process !== 'undefined' && process.env) {
      return process.env[key] || process.env[`NEXT_PUBLIC_${key}`];
    }
    return undefined;
  }

  /**
   * Parse boolean value from string
   */
  private parseBoolean(value: string): boolean {
    const normalized = value.toLowerCase().trim();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }

  /**
   * Check if a feature flag is enabled
   */
  public isEnabled(flag: FeatureFlag): boolean {
    if (!this.initialized) {
      this.initialize();
    }
    return this.flags[flag] ?? FEATURE_FLAG_CONFIG[flag].defaultValue;
  }

  /**
   * Get the current value of a feature flag
   */
  public getValue(flag: FeatureFlag): boolean {
    return this.isEnabled(flag);
  }

  /**
   * Get all current feature flag values
   */
  public getAll(): FeatureFlagValues {
    if (!this.initialized) {
      this.initialize();
    }
    return { ...this.flags };
  }

  /**
   * Check if all provided flags are enabled
   */
  public areEnabled(flags: FeatureFlag[]): boolean {
    return flags.every(flag => this.isEnabled(flag));
  }

  /**
   * Check if any of the provided flags are enabled
   */
  public isAnyEnabled(flags: FeatureFlag[]): boolean {
    return flags.some(flag => this.isEnabled(flag));
  }

  /**
   * Override flag values (for testing)
   */
  public override(overrides: Partial<FeatureFlagValues>): void {
    this.flags = { ...this.flags, ...overrides };

    if (this.config.debug) {
      console.log('[FeatureFlags] Applied overrides:', overrides);
    }
  }

  /**
   * Reset to default values
   */
  public reset(): void {
    this.flags = {} as FeatureFlagValues;
    this.initialized = false;

    if (this.config.debug) {
      console.log('[FeatureFlags] Reset to defaults');
    }
  }

  /**
   * Reset singleton instance (for testing)
   */
  public static resetInstance(): void {
    FeatureFlagManager.instance = null;
  }
}
